import React, { useContext, useEffect, useRef, useState } from "react";
import DynamicChart from "./DynamicChart";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import AppContext from "../../AppContext";
import CustomCard from "./CustomCard";
import CustomTable from "./CustomTable";
import { BarChart2, Table } from "lucide-react";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { BASE_CHAT_URL } from "../../constant";

const CustomChatComponent = ({ isExpanded, setIsExpanded }) => {
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const chatContainerRef = useRef(null);
  const { authCtx } = useContext(AppContext);
  const [viewMode, setViewMode] = useState("chart");
  const [sampleMessages, setSampleMessages] = useState([]);
  const [messages, setMessages] = useState([]);

  const handleOnSendMessage = async (message) => {
    const userMessage = {
      type: "text",
      author: authCtx.profile.name,
      text: message,
      timestamp: new Date(),
    };

    // Add user message immediately
    setMessages((prevMessages) => [...prevMessages, userMessage]);

    const loadingMessage = {
      author: "RMS",
      text: "",
      type: "loading", // Special type for loading indicator
      timestamp: new Date(),
    };

    setMessages((prevMessages) => [...prevMessages, loadingMessage]);

    // Prepare the payload for the API call
    const payload = {
      query: message,
      domain_id: authCtx.profile.companyId,
      session_id: authCtx.profile.id,
      user: authCtx.profile.name,
    };

    try {
      const res = await axios.post(`${BASE_CHAT_URL}/api/query`, payload);

      if (res.status === 200) {
        let botMessage;
        const chartConfig = transformChatbotGraphData(res.data);
        botMessage = {
          author: "RMS",
          text:
            res.data.answer || "I'm sorry, I couldn't process that request.",
          ...res.data,
          timestamp: new Date(),
          ...chartConfig,
        };
        console.log(chartConfig, "config");
        setMessages((prevMessages) => [
          ...prevMessages.slice(0, -1), // Remove the loading message
          botMessage,
        ]);
        return;
      } else {
        console.error("Error fetching bot response:");
        // Handle error case
        setMessages((prevMessages) => [
          ...prevMessages.slice(0, -1), // Remove the loading message
          {
            type: "text",
            author: "RMS",
            text: "I'm sorry, but I couldn't process your request. Please try again later",
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching bot response:", error);
      // Handle error case
      setMessages((prevMessages) => [
        ...prevMessages.slice(0, -1), // Remove the loading message
        {
          type: "text",
          author: "RMS",
          text: "I'm sorry, but I couldn't process your request. Please try again later",
          timestamp: new Date(),
        },
      ]);
    }
  };

  function isProbablyDate(str) {
    return (
      typeof str === "string" &&
      (/^\d{4}-\d{2}-\d{2}$/.test(str) || // ISO format
        /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str) || // MM/DD/YYYY
        /^\d{4}\/\d{1,2}\/\d{1,2}$/.test(str) || // YYYY/MM/DD
        /^[A-Za-z]{3,9} \d{1,2}, \d{4}$/.test(str)) // "April 19, 2025"
    );
  }

  const transformChatbotGraphData = (apiResponse) => {
    if (
      !apiResponse ||
      !apiResponse.graph ||
      !Array.isArray(apiResponse.graph.data)
    ) {
      return null;
    }

    const rawData = apiResponse.graph.data;

    // Determine if X values are dates
    const isTimeBased = isProbablyDate(rawData[0]?.label);

    const chartData = rawData.map((item) => ({
      date: isTimeBased ? new Date(item.label) : undefined,
      label: !isTimeBased ? item.label : undefined,
      value: item.value,
    }));

    const values = rawData.map((item) => item.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const interval = Math.ceil((maxValue - minValue) / 5) || 1;

    const chartConfig = [
      {
        xName: isTimeBased ? "date" : "label",
        yName: "value",
        name: apiResponse.graph.title || "Data Series",
        type: apiResponse.graph.chartType === "line" ? "Line" : "Column", // fallback to Column
        color: "#00bcd4",
      },
    ];

    return {
      chartData,
      chartConfig,
      minValue: minValue - interval,
      maxValue: maxValue + interval,
      interval,
      xAxisType: isTimeBased ? "DateTime" : "Category",
    };
  };

  const getSampleMessages = async () => {
    const payload = {
      domain_id: authCtx.profile.companyId,
    };
    try {
      const res = await axios.post(
        `${BASE_CHAT_URL}/api/domain-questions`,
        payload
      );
      if (
        res.status === 200 &&
        res.data.sample_questions &&
        res.data.sample_questions.length > 0
      ) {
        setSampleMessages(res.data.sample_questions);
      }
    } catch (error) {
      console.error("Error fetching bot response:", error);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  const toggleViewMode = () => {
    setViewMode((prevMode) => (prevMode === "chart" ? "table" : "chart"));
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  const handleSendMessage = (message = inputMessage) => {
    if (message.trim()) {
      setIsSending(true);
      handleOnSendMessage(message);
      setInputMessage("");
      setTimeout(() => setIsSending(false), 200);
    }
  };

  const renderMessageContent = (msg) => {
    return (
      <>
        {/* Show markdown explanation */}
        <div className="text-white mb-4">
          <ReactMarkdown >{msg.answer || msg.text}</ReactMarkdown>
        </div>

        {/* If there's a graph included */}
        {msg.graph && (
          <div className="w-full h-1/3">
            <DynamicChart isExpanded data={msg} />
          </div>
        )}
        {msg.cards && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {msg.cards.map((card, index) => (
              <CustomCard
                key={index}
                title={card.title}
                value={card.value}
                label={card.label}
                icon={card.icon}
              />
            ))}
          </div>
        )}
      </>
    );
  };
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [isExpanded]);

  useEffect(() => {
    getSampleMessages();
  }, []);

  const renderWelcomeMessage = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-red-700 to-orange-500 text-transparent bg-clip-text">
        Welcome {authCtx.profile.name},
        <br />
        How can I help you today?
      </h2>
      <div className="grid grid-cols-1 gap-4 w-full max-w-md">
        {sampleMessages &&
          sampleMessages.map((question, index) => (
            <button
              key={index}
              onClick={() => handleSendMessage(question)}
              className="bg-gray-700 text-white p-3 rounded-lg text-sm hover:bg-gray-600 transition-transform transform hover:scale-105 duration-300"
            >
              {question}
            </button>
          ))}
      </div>
    </div>
  );

  return (
    <div
      style={{
        width: isExpanded ? "55%" : "450px",
        height: isExpanded ? "90vh" : "82vh",
      }}
      className={`flex flex-col border border-gray-600 mx-auto bg-neutral-800 shadow-2xl rounded-2xl chat-component-transition backdrop-blur-md bg-opacity ${
        isExpanded ? "bg-opacity-90 backdrop-blur-md mt-10 " : ""
      }`}
    >
      <div className="bg-neutral-900 rounded-t-2xl rounded-b justify-between text-white px-4 py-2 flex items-center chat-header-transition">
        <span className="text-lg flex gap-2 items-center font-semibold">
          <img
            style={{
              borderRadius: "100%",
              border: "none",
              fontSize: "14px",
              cursor: "pointer",
              height: "45px",
              width: "45px",
            }}
            src="ai_icon.svg"
            className="mx-auto top-0 h-12 w-12 cursor-pointer"
            alt="Chat Icon"
          />
          Apollo AI Agent
        </span>
        <button
          onClick={toggleExpand}
          className="text-white hover:text-gray-400 transition-all duration-200"
        >
          {!isExpanded ? (
            <img src="expand.svg" className="h-4 w-4" />
          ) : (
            <img src="zoomout.svg" className="h-4 w-4" />
          )}
        </button>
      </div>
      <div ref={chatContainerRef} className="flex-1 p-4 overflow-y-auto">
        {messages.length === 0 ? (
          renderWelcomeMessage()
        ) : (
          <TransitionGroup>
            {messages.map((msg, index) => (
              <CSSTransition key={index} timeout={500} classNames="message">
                <div
                  className={`mb-4 flex ${
                    msg.author === authCtx.profile.name
                      ? "justify-end"
                      : "justify-start"
                  }`}
                >
                  <div className="flex items-start">
                    {msg.type === "loading" ? (
                      <div className="spinner">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: isExpanded ? "40vw" : "380px",
                        }}
                        className={`rounded-lg px-4 py-2 transition-transform ${
                          msg.author === authCtx.profile.name
                            ? "bg-blue-600 text-white hover:bg-blue-500"
                            : "bg-gray-600 text-white hover:bg-gray-500"
                        } shadow-lg hover:shadow-xl transform hover:-translate-y-1 message-bubble`}
                      >
                        {renderMessageContent(msg)}
                      </div>
                    )}
                  </div>
                </div>
              </CSSTransition>
            ))}
          </TransitionGroup>
        )}
      </div>
      <div className="p-4 flex items-center border-t border-gray-700">
        <input
          type="text"
          value={inputMessage}
          onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
          onChange={handleInputChange}
          className="flex-1 px-4 py-2 bg-neutral-700 text-white border border-gray-600 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300"
          placeholder="Type a message..."
        />
        <button
          onClick={() => handleSendMessage()}
          className={`ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg transition-transform duration-200 transform ${
            isSending ? "scale-95" : "hover:scale-105"
          }`}
        >
          <svg
            width="20px"
            height="22px"
            fill="white"
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>send-solid</title>
            <g id="Layer_2" data-name="Layer 2">
              <g id="invisible_box" data-name="invisible box">
                <rect width="48" height="48" fill="none" />
              </g>
              <g id="icons_Q2" data-name="icons Q2">
                <path d="M44.9,23.2l-38-18L6,5A2,2,0,0,0,4,7L9.3,23H24a2.1,2.1,0,0,1,2,2,2,2,0,0,1-2,2H9.3L4,43a2,2,0,0,0,2,2l.9-.2,38-18A2,2,0,0,0,44.9,23.2Z" />
              </g>
            </g>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CustomChatComponent;
